
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWindowDimensions, View } from 'react-native';
import {Calendar, DateRangeFormatFunction, dayjsLocalizer, SlotInfo, ToolbarProps, Views} from 'react-big-calendar';

// @ts-ignore: side-effect import for react-big-calendar CSS without module declarations
import 'react-big-calendar/lib/css/react-big-calendar.css';
// @ts-ignore: side-effect import for react-big-calendar CSS without module declarations
import './custom-calendar.css';

import dayjs from 'dayjs'
import styles from './CalendrierUniversel.styles'
import { Absence } from '../../api/Absence/types';
import { DemandeAbsenceType, EventType, FormattedEventType } from '../../api/DemandeAbsence/types';
import DateCellWrapper from './Web/DateCellWrapper.component.web';
import MonthDateHeader from './Web/MonthDateHeader.component';
import Toolbar from './Web/Toolbar/Toolbar';
import { capitalize, ecritureContrastee, separeJours } from '../../lib/generique';
import { useCalendrierUniverselState } from './CalendrierUniversel.states'
import { CalendrierUniverselProps } from './CalendrierUniversel.types'
import { DetailDemandeAbsence } from '../Absences/DetailDemandeAbsence/DetailDemandeAbsence.component';
import DetailAbsence from '../Absences/DetailAbsence/DetailAbsence.component';
import { useDemandeAbsenceActions } from '../../modules/DemandeAbsence';
import { useNotify } from '../Notification';
import AgendaWeb from './Web/AgendaWeb.component';
import AddButton from '../AddButton/AddButton';

dayjs.locale('fr');

const localizer = dayjsLocalizer(dayjs)

export const CalendrierUniversel: React.FC<CalendrierUniverselProps> = ({
  maxDate,
  markedDates,
  mode,
  onAddAbsence,
  onDayPress,
  navigation,
}) => {
  const notify = useNotify();
  const { width } = useWindowDimensions();
  const isTablet = width >= 768;
  const {
    donnees,
    selectedAbsence,
    setDonnees,
    isFerie,
    toggleVacances,
    chargementDonnees,
    closeAbsenceDetail,
    handleVisualiseAbsence,
  } = useCalendrierUniverselState();

  const {
    selectedDemande,
    closeDemandeDetail,
    handleAnnule,
    handleValide,
    handleVisualiseDemande,
  } = useDemandeAbsenceActions(navigation, notify, (identifiant) => {
    setDonnees(prev => ({...prev, demandes: prev.demandes.filter((demande) => demande.identifiant !== identifiant)}))
  })

  const [view, setView] = useState<string>(Views.MONTH)
  const [currentDate, setCurrentDate] = useState<Date>(new Date())

  const rerenderKey = useMemo(() => markedDates ? JSON.stringify(markedDates) : '0', [markedDates])

  const feriesStyle = useCallback((date: Date) => ({className : isFerie(date) ? 'ferie' : ''}), [isFerie])

  // Fonction non mémoïsée pour s'assurer qu'elle utilise toujours les dernières valeurs
  const getDayProps = (date: Date) => ({ className: isFerie(date) ? 'ferie' : '' })

  const scrollToTime = useMemo(() => dayjs(currentDate).hour(6).toDate(), [currentDate])

  const handleDayPress = useCallback((slot: SlotInfo) => {
    const date = slot.start
    if(mode !== "month" || !(markedDates?.[dayjs(date).format('YYYY-MM-DD')]?.disabled || dayjs().isAfter(date, 'day') || (maxDate && dayjs(maxDate).isBefore(date, 'day')))){
      if(onDayPress){
        onDayPress({dateString: date.toDateString()})
      } else {
        setView(Views.DAY);
        setCurrentDate(date);
      }
    }
  }, [markedDates, maxDate, mode, onDayPress])

  const toolbar = useCallback((props: ToolbarProps<EventType>) =>
    <Toolbar {...props} mode={mode || 'global'} vacances={donnees.vacances} isFerie={isFerie} toggleVacances={toggleVacances} />, [donnees.vacances, isFerie, mode, toggleVacances]
  )

  const dateCellWrapper = useCallback(({value}: {value:Date}) => {
    return <DateCellWrapper vacances={donnees.vacances} date={value} view={view as any} markedDates={markedDates} />
  }, [donnees.vacances, view, markedDates])

  const dateHeader = useCallback(({date}: {date: Date}) => {
    return <MonthDateHeader date={date} maxDate={maxDate} markedDates={markedDates} mode={mode} className={feriesStyle(date).className} />
  }, [feriesStyle, markedDates, maxDate, mode])

  const dayRangeHeaderFormat: DateRangeFormatFunction = useCallback(({start, end}) => {
    if(isTablet){
      return `${dayjs(start).format('DD MMMM YYYY')} - ${dayjs(end).format('DD MMMM YYYY')}`
    }
    return `${dayjs(start).format('DD/MM/YY')} - ${dayjs(end).format('DD/MM/YY')}`
  }, [isTablet])

  const events = useMemo<FormattedEventType[]>(() => {
    const absences: FormattedEventType[] = donnees.absences.flatMap((absence) => {
      const start = dayjs(absence.dateDebut)
          .hour(absence.heureDebut)
          .minute(Math.round((absence.heureDebut % 1) * 100))
          .toDate()
      const end = dayjs(absence.dateFin)
          .hour(absence.heureFin)
          .minute(Math.round((absence.heureFin % 1) * 100))
          .toDate()
      const backgroundColor = absence.natureAbsence?.couleurHexa || '#000000';
      const style = {
          backgroundColor,
          color: ecritureContrastee(backgroundColor),
          fontWeight: 'bold',
        }
      if(view === Views.MONTH || view === Views.AGENDA){
        return {...absence, type: 'absence', start, end, style, couleur: absence.natureAbsence?.couleurHexa || '#000000'}
      }
      return separeJours(start, end, { ...absence, type: 'absence', style, couleur: absence.natureAbsence?.couleurHexa || '#000000'})
    })
    const demandes: FormattedEventType[] = donnees.demandes.flatMap((demande) => {
      const start = dayjs(demande.dateDebut)
          .hour(demande.heureDebut)
          .minute(Math.round((demande.heureDebut % 1) * 100))
          .toDate()
      const end = dayjs(demande.dateFin)
          .hour(demande.heureFin)
          .minute(Math.round((demande.heureFin % 1) * 100))
          .toDate()
      const backgroundColor = demande.natureAbsence?.couleurHexa || '#000000';
      const style = {
          backgroundColor,
          color: ecritureContrastee(backgroundColor),
          opacity: '60%',
          fontWeight: 'bold',
        }
      if(view === Views.MONTH || view === Views.AGENDA){
        return {...demande, type: 'demandeAbsence', start, end, style, couleur: demande.natureAbsence?.couleurHexa || '#000000'}
      }
      return separeJours(start, end, {...demande, type: 'demandeAbsence', style, couleur: demande.natureAbsence?.couleurHexa || '#000000'})
    })
    return absences.concat(demandes);
  }, [donnees, view])

  const handleEventPress = useCallback((item: EventType) => {
    if(mode!=='month'){
      const {identifiant, type} = item
      const foundEvent = events.find((event) => identifiant === event.identifiant && type === event.type)
      if(foundEvent){
        if(foundEvent.type === 'demandeAbsence'){
          handleVisualiseDemande(foundEvent as DemandeAbsenceType)
        } else if(foundEvent.type === 'absence') {
          handleVisualiseAbsence(foundEvent as Absence)
        } else {
          notify.error('Erreur dans le format de données', 2000)
        }
      } else {
        notify.error('Données introuvables', 2000)
      }
    }
  }, [events, handleVisualiseAbsence, handleVisualiseDemande, mode, notify])

  useEffect(() => {
    chargementDonnees(currentDate);
  }, [chargementDonnees, currentDate])

  const messages = {
    allDay: 'Toute la journée',
    previous: 'Précédent',
    next: 'Suivant',
    today: 'Aujourd’hui',
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
    noEventsInRange: 'Aucun événement dans cette période',
    showMore: (total: number) => `+${total}`,
  };

  return (
    <>
      <View style={styles.webCalendarContainer}>
        <Calendar
          key={rerenderKey}
          localizer={localizer}
          view={view as any}
          date={currentDate}
          events={events}
          eventPropGetter={(event) => ({style: event.style})}
          messages={messages}
          onNavigate={setCurrentDate}
          onView={setView}
          onSelectSlot={handleDayPress}
          scrollToTime={scrollToTime}
          dayPropGetter={getDayProps}
          selectable
          longPressThreshold={10} 
          titleAccessor={(e) => (isTablet || view === Views.DAY ? e.natureAbsence?.libelle || '' : '')}
          components={{
            dateCellWrapper,
            month: { dateHeader },
            toolbar
          }}
          formats={{
            eventTimeRangeFormat: ({start, end}) => isTablet || view === Views.DAY ? `${dayjs(start).format('HH:mm')} - ${dayjs(end).format('HH:mm')}` : '',
            dayFormat: (date, _, __) => capitalize(dayjs(date).format(`dd${isTablet ? 'dd' : ''} D`)),
            dayHeaderFormat: (date, _, __) => capitalize(dayjs(date).format('dddd D MMMM YYYY')),
            dayRangeHeaderFormat,
            weekdayFormat: (date, _, __) => capitalize(dayjs(date).format(`ddd${isTablet ? 'd' : ''}`)),
          }}
          views={{
            day: true,
            week: true,
            month: true,
            agenda: AgendaWeb,
          }}
          onSelectEvent={(slotInfo) => handleEventPress(slotInfo)}
        />
      </View>
      {selectedDemande && (
        <DetailDemandeAbsence
          onClose={closeDemandeDetail}
          selectedDemande={selectedDemande}
          handleAnnule={handleAnnule}
          handleValide={handleValide}
        />
      )}
      <DetailAbsence
        onClose={closeAbsenceDetail}
        selectedAbsence={selectedAbsence}
      />
      {onAddAbsence && <AddButton onPress={onAddAbsence}/>}
    </>
  )
}

export default CalendrierUniversel;
